import React, { useState } from 'react';
import mammoth from "mammoth";
import { getDocument, GlobalWorkerOptions } from "pdfjs-dist";
import JSZip from "jszip";
import axios from 'axios';
import './App.css';
import { useAuth0 } from '@auth0/auth0-react';

GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";

function App() {
  const [userPrompt, setUserPrompt] = useState('');
  const [result, setResult] = useState('');
  const [fileContent, setFileContent] = useState<string>("");
  const { getAccessTokenSilently } = useAuth0(); // ✅ Get Auth0 token

  const extractTextFromDocx = async (arrayBuffer: ArrayBuffer): Promise<string> => {
    const buffer = new Uint8Array(arrayBuffer);
    const result = await mammoth.extractRawText({ arrayBuffer: buffer });
    return result.value;
  };

  const extractTextFromZip = async (arrayBuffer: ArrayBuffer) => {
    const zip = await JSZip.loadAsync(arrayBuffer);
    let extractedContent = "";

    for (const filename of Object.keys(zip.files)) {
      const file = zip.files[filename];

      if (file.dir) continue; // Skip directories

      if (filename.endsWith(".txt")) {
        const content = await file.async("string");
        extractedContent += `\n\n--- ${filename} ---\n${content}`;
      } else if (filename.endsWith(".docx")) {
        const content = await file.async("arraybuffer");
        const docxText = await extractTextFromDocx(content);
        extractedContent += `\n\n--- ${filename} ---\n${docxText}`;
      } else if (filename.endsWith(".pdf")) {
        const content = await file.async("arraybuffer");
        const pdfText = await extractTextFromPDF(content);
        extractedContent += `\n\n--- ${filename} ---\n${pdfText}`;
      } else {
        extractedContent += `\n\n--- ${filename} ---\nUnsupported file type.`;
      }
    }

    return extractedContent;
  };

  const extractTextFromPDF = async (arrayBuffer: ArrayBuffer): Promise<string> => {
    const pdf = await getDocument({ data: arrayBuffer }).promise;
    let text = "";

    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const content = await page.getTextContent();
      text += content.items.map((item: any) => item.str).join(" ");
    }

    return text;
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    const fileType = file.type;

    reader.onload = async (e: ProgressEvent<FileReader>) => {
      const result = e.target?.result;
      if (!result) return;

      try {
        if (fileType === "application/pdf" && result instanceof ArrayBuffer) {
          const pdfText = await extractTextFromPDF(result);
          setFileContent(pdfText);
        } else if (
          fileType === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" &&
          result instanceof ArrayBuffer
        ) {
          const docxData = await mammoth.extractRawText({ arrayBuffer: result });
          setFileContent(docxData.value);
        } else if (fileType === "text/plain" && typeof result === "string") {
          setFileContent(result);
        } else if (fileType === "application/zip" || file.name.endsWith(".zip")) {
          const zipText = await extractTextFromZip(result as ArrayBuffer);
          setFileContent(zipText);
        } else {
          setFileContent("Unsupported file type.");
        }
      } catch (err) {
        setFileContent("Error processing file: " + (err as Error).message);
      }
    };

    if (fileType === "text/plain") {
      reader.readAsText(file);
    } else {
      reader.readAsArrayBuffer(file);
    }
  };


  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
  
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: { // ✅ Fix: Wrap audience inside authorizationParams
          audience: "https://docuquery.aiptf.com/aiquery",
          scope: "openid profile email",
        },
      });
  
      const response = await axios.get("/aiquery", {
        params: {
          userPrompt,
          transcriptFileContent: fileContent || "No transcript uploaded",
        },
        headers: {
          Authorization: `Bearer ${token}`, // ✅ Send Auth0 token in header
        },
      });
  
      setResult(response.data);
    } catch (error) {
      console.error("Error making API request:", error);
    }
  };

  return (
    <div className="form-container">
    <form onSubmit={handleSubmit}>
      <label className="form-label" htmlFor="file-upload">
          Upload your document (PDF, DOCX, TXT, and ZIP files are supported):
        </label>
        <input
          type="file"
          id="file-upload"
          onChange={handleFileUpload}
          accept=".pdf,.docx,.txt,.zip"
        />
      <br /><br /><br />
      <label className="form-label" htmlFor="user-prompt">Ask AI to Find Information for You!</label>
      <input
        id="user-prompt"
        type="text"
        value={userPrompt}
        onChange={(event) => setUserPrompt(event.target.value)}
        placeholder="Enter user prompt..."
        className="form-input"
      />
      <br />
      <button type="submit" className="form-button submit-button">Send Request</button>
    </form>
    {result && (
      <div>
        <h2>Results:</h2>
        <p>{result}</p>
      </div>
    )}
  </div>
  );
}

export default App;
